import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  Grid, 
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Preview as PreviewIcon, 
  Clear as ClearIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import SysEditor from '../components/syseditor';

const TextEditorPage: React.FC = () => {
  const [content, setContent] = useState(`
    <h2>Bem-vindo ao Editor de Texto Personalizado! 🎉</h2>
    
    <p>Este é um editor de texto rico desenvolvido completamente do zero para o sistema SysUp. Você pode usar todas as funcionalidades disponíveis:</p>
    
    <h3>Funcionalidades Disponíveis:</h3>
    <ul>
      <li><strong>Formatação de Texto:</strong> Negrito, itálico e muito mais!</li>
      <li><strong>Tipografia:</strong> 13 tipos de fonte diferentes</li>
      <li><strong>Tamanhos:</strong> 16 tamanhos de fonte (8px até 72px)</li>
      <li><strong>Alinhamento:</strong> Esquerda, centro, direita e justificar</li>
      <li><strong>Imagens:</strong> Upload de arquivos ou inserção via URL</li>
      <li><strong>Tabelas:</strong> Editor completo com configurações avançadas</li>
    </ul>
    
    <p style="text-align: center;"><em>Experimente todas as funcionalidades na barra de ferramentas acima!</em></p>
    
    <h3>Atalhos de Teclado:</h3>
    <p>• <strong>Ctrl+B</strong> - Negrito</p>
    <p>• <strong>Ctrl+I</strong> - Itálico</p>
    <p>• <strong>Ctrl+U</strong> - Sublinhado</p>
  `);
  
  const [showPreview, setShowPreview] = useState(false);
  const [showSaveAlert, setShowSaveAlert] = useState(false);

  const handleSave = () => {
    setShowSaveAlert(true);
    localStorage.setItem('editor_content', content);
    
    setTimeout(() => {
      setShowSaveAlert(false);
    }, 3000);
  };

  const handleClear = () => {
    if (window.confirm('Tem certeza que deseja limpar todo o conteúdo?')) {
      setContent('');
    }
  };

  const handleExport = () => {
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'documento.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getContentStats = () => {
    const textContent = content.replace(/<[^>]*>/g, '');
    const wordCount = textContent.trim() ? textContent.trim().split(/\s+/).length : 0;
    const charCount = textContent.length;
    const htmlSize = new Blob([content]).size;
    
    return { wordCount, charCount, htmlSize };
  };

  const stats = getContentStats();

  return (
    <>
      <Helmet>
        <title>Editor de Texto - SysUp</title>
        <meta name="description" content="Editor de texto rico personalizado do sistema SysUp" />
      </Helmet>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper elevation={1} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            📝 Editor de Texto Personalizado
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Editor de texto rico desenvolvido do zero especialmente para o SysUp. 
            Crie documentos, contratos, relatórios e muito mais com todas as ferramentas necessárias.
          </Typography>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  {stats.wordCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Palavras
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  {stats.charCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Caracteres
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  {(stats.htmlSize / 1024).toFixed(1)} KB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tamanho
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  HTML
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Formato
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {showSaveAlert && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Conteúdo salvo com sucesso! 💾
          </Alert>
        )}

        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              color="primary"
            >
              Salvar
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              color="success"
            >
              Exportar HTML
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              color="error"
            >
              Limpar Tudo
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={showPreview ? 6 : 12}>
            <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  📝 Editor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use a barra de ferramentas para formatar seu texto
                </Typography>
              </Box>
              
                              <SysEditor
                value={content}
                onChange={setContent}
                placeholder="Comece a digitar seu documento aqui..."
                height={600}
              />
            </Paper>
          </Grid>

          {showPreview && (
            <Grid item xs={12} lg={6}>
              <Paper elevation={2} sx={{ p: 0, borderRadius: 2, height: '100%' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    👁️ Preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visualização do conteúdo formatado
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 3, 
                    minHeight: 600,
                    maxHeight: 600,
                    overflow: 'auto',
                    backgroundColor: 'white',
                    fontFamily: 'inherit',
                    lineHeight: 1.6
                  }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </Paper>
            </Grid>
          )}
        </Grid>

        <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            🛠️ Funcionalidades Disponíveis
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Formatação de Texto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Negrito (Ctrl+B)<br/>
                  • Itálico (Ctrl+I)<br/>
                  • Sublinhado (Ctrl+U)
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Tipografia
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • 13 tipos de fonte<br/>
                  • 16 tamanhos diferentes<br/>
                  • Interface visual
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Alinhamento
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Esquerda<br/>
                  • Centro<br/>
                  • Direita<br/>
                  • Justificar
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </>
  );
};

export default TextEditorPage; 